import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { PauseCircle, PlayCircle, AlertTriangle } from "lucide-react";
import { cn } from "../lib/utils";
import { Tasks, apiErrorMessage } from "../lib/api";
import { fmtDate } from "../lib/auditFormat";

/**
 * Mark Delay / Hold mechanism for Accounts, Insurance, Road Tax & HSRP tasks.
 *
 * Renders one of two affordances based on task.sla_paused:
 *   - Mark Delay / Hold (opens reason dialog → POST /tasks/{id}/sla_pause)
 *   - Resume Processing (POST /tasks/{id}/sla_resume)
 *
 * On hold the SLA engine returns status=paused/color=blue; the timer stops
 * accruing elapsed minutes. Reason + remarks + expected_resume_on are stored
 * on the task and surfaced in the Activity Timeline via the sla_paused audit.
 */

export const HOLD_REASONS = [
  { value: "customer_docs_pending",   label: "Customer Documents Pending" },
  { value: "customer_not_available",  label: "Customer Not Available" },
  { value: "customer_requested_hold", label: "Customer Requested Hold" },
  { value: "rto_delay",               label: "RTO Delay" },
  { value: "insurance_portal_issue",  label: "Insurance Portal Issue" },
  { value: "hsrp_portal_issue",       label: "HSRP Portal Issue" },
  { value: "system_issue",            label: "System Issue" },
  { value: "external_dependency",     label: "External Dependency" },
  { value: "other",                   label: "Other" },
];

const REASON_LABEL = Object.fromEntries(HOLD_REASONS.map((r) => [r.value, r.label]));

export function getReasonLabel(value) {
  return REASON_LABEL[value] || value || "—";
}

export function HoldBadge({ task, className }) {
  if (!task?.sla_paused) return null;
  const reason = task.sla_pause_reason;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700 ring-1 ring-blue-200",
        className,
      )}
      data-testid="hold-badge"
      title={`On Hold · ${getReasonLabel(reason)}${task.sla_pause_remarks ? " · " + task.sla_pause_remarks : ""}`}
    >
      <PauseCircle className="h-3 w-3" />
      On Hold
      {reason && <span className="opacity-80">· {getReasonLabel(reason)}</span>}
    </span>
  );
}

export function HoldDetailsCard({ task }) {
  if (!task?.sla_paused) return null;
  return (
    <div
      className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-sm text-blue-900"
      data-testid="hold-details"
    >
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-blue-700">
        <PauseCircle className="h-3.5 w-3.5" /> Task on hold
      </div>
      <div className="mt-1 text-sm">
        <strong>{getReasonLabel(task.sla_pause_reason)}</strong>
        {task.sla_pause_remarks && (
          <> — <span className="text-blue-800">{task.sla_pause_remarks}</span></>
        )}
      </div>
      {task.sla_pause_expected_resume_on && (
        <div className="mt-1 text-[11px] text-blue-700">
          Expected resume: <strong>{fmtDate(task.sla_pause_expected_resume_on)}</strong>
        </div>
      )}
    </div>
  );
}

/**
 * Inline button group for placement next to other action buttons.
 *
 * Usage:
 *   <TaskHoldControl task={task} userId={user?.id} onChanged={reload} />
 */
export function TaskHoldControl({ task, userId, onChanged, size = "sm", className }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!task || task.status === "completed") return null;

  const onResume = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await Tasks.slaResume(task.id);
      toast.success("Processing resumed · SLA timer restarted");
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to resume"); }
    finally { setBusy(false); }
  };

  if (task.sla_paused) {
    return (
      <Button
        size={size}
        variant="outline"
        onClick={onResume}
        disabled={busy}
        className={cn("gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50", className)}
        data-testid="task-resume-btn"
      >
        <PlayCircle className="h-4 w-4" />
        Resume Processing
      </Button>
    );
  }

  return (
    <>
      <Button
        size={size}
        variant="outline"
        onClick={() => setOpen(true)}
        className={cn("gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50", className)}
        data-testid="task-hold-btn"
      >
        <PauseCircle className="h-4 w-4" />
        Mark Delay / Hold
      </Button>
      <HoldDialog
        open={open}
        onOpenChange={setOpen}
        task={task}
        userId={userId}
        onSaved={onChanged}
      />
    </>
  );
}

function HoldDialog({ open, onOpenChange, task, userId, onSaved }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [resumeOn, setResumeOn] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset state when reopened.
  React.useEffect(() => {
    if (open) { setReason(""); setNotes(""); setResumeOn(""); }
  }, [open]);

  const notesRequired = reason === "other";
  const canSave = !!reason && (!notesRequired || notes.trim().length > 0);

  const onSave = async () => {
    if (!canSave) {
      if (!reason) toast.error("Please select a delay reason");
      else if (notesRequired) toast.error("Notes are required when reason is 'Other'");
      return;
    }
    setSaving(true);
    try {
      await Tasks.slaPause(task.id, {
        reason,
        remarks: notes.trim(),
        expected_resume_on: resumeOn || null,
        user_id: userId,
      });
      toast.success("Task put on hold · SLA paused");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to put task on hold");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hold-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PauseCircle className="h-5 w-5 text-amber-600" /> Delay / Hold Reason
          </DialogTitle>
          <DialogDescription>
            The SLA timer will stop accruing until this task is resumed. All hold
            actions are tracked in the Activity Timeline.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Delay Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="hold-reason-select">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {HOLD_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`hold-reason-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              Notes / Remarks {notesRequired && <span className="text-rose-600">*</span>}
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter reason details"
              rows={3}
              data-testid="hold-notes-input"
            />
            {notesRequired && (
              <p className="flex items-center gap-1 text-[11px] text-rose-600">
                <AlertTriangle className="h-3 w-3" />
                Notes are required when reason is "Other".
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Expected Resume Date <span className="text-slate-400">(optional)</span></Label>
            <Input
              type="date"
              value={resumeOn}
              onChange={(e) => setResumeOn(e.target.value)}
              data-testid="hold-resume-date-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hold-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={!canSave || saving}
            className="gap-1.5"
            data-testid="hold-save-btn"
          >
            <PauseCircle className="h-4 w-4" />
            {saving ? "Saving…" : "Save & Pause"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TaskHoldControl;
